// ────────────────────────────────────────────────────────────────
// Imports
// ────────────────────────────────────────────────────────────────

import { parseEventLogs } from 'viem';
import React, { useState, useEffect } from 'react';
import { createPublicClient, createWalletClient, custom, http, parseEther } from 'viem';
import artifact from './MyContractABI.json';
import TransactionHistory from './TransactionHistory';
import { ThemeProvider, useTheme } from './ThemeContext';
import io from 'socket.io-client';
import logo from './logo.svg';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AdminPage from './pages/AdminPage';

// ────────────────────────────────────────────────────────────────
// Contract & Network Configuration
// ────────────────────────────────────────────────────────────────

// Debug flag
const DEBUG = false;

const CONTRACT_ADDRESS = '0x42e0cbF666d77af36C34b7Bd04BC6120FC1c6079';

const MyContractABI = artifact.abi;

const socket = io('https://coliving.dimikog.org', {
  transports: ['websocket'],
  autoConnect: false,
});

const besuEduNet = {
  id: 424242,
  name: 'Besu EduNet',
  network: 'besu',
  nativeCurrency: {
    name: 'EDU-D',
    symbol: 'EDU-D',
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: ['https://rpc.dimikog.org/rpc/'],
    },
  },
};

// ────────────────────────────────────────────────────────────────
// viem Clients (publicClient, walletClient)
// ────────────────────────────────────────────────────────────────

// 🔗 Σταθεροί viem clients (έξω από το component)
const publicClient = createPublicClient({
  chain: besuEduNet,
  transport: http(),
});

const walletClient = createWalletClient({
  chain: besuEduNet,
  transport: custom(window.ethereum),
});

// ────────────────────────────────────────────────────────────────
// Utility Helpers (ensureBesuNetwork, safeStringify, logs)
// ────────────────────────────────────────────────────────────────

// ✅ Helper: βεβαιώσου ότι το wallet είναι στο Besu EduNet
const ensureBesuNetwork = async () => {
  try {
    await walletClient.switchChain({ id: 424242 });
  } catch (err) {
    console.error('Αποτυχία switch σε Besu EduNet:', err);
    throw new Error('Βεβαιώσου ότι το πορτοφόλι σου επιτρέπει αλλαγή δικτύου σε Besu EduNet.');
  }
};

// 🔧 Helper function για BigInt → String
const safeStringify = (obj) =>
  JSON.stringify(obj, (key, value) =>
    typeof value === 'bigint' ? value.toString() : value
  );

// ────────────────────────────────────────────────────────────────
// Component State
// ────────────────────────────────────────────────────────────────

function AppMainContent(props) {
  const { account } = props;
  const [taskDescription, setTaskDescription] = useState('');
  const [taskValue, setTaskValue] = useState('0.01');
  const [tasks, setTasks] = useState([]);
  const [loadTasks, setLoadTasks] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [selectedSpaceType, setSelectedSpaceType] = useState(0);
  const [beforePhoto, setBeforePhoto] = useState(null);
  const [afterPhoto, setAfterPhoto] = useState(null);
  const [completeTaskId, setCompleteTaskId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [duringPhoto, setDuringPhoto] = useState(null);
  const [positionX, setPositionX] = useState('');
  const [positionY, setPositionY] = useState('');
  const [positionZ, setPositionZ] = useState('');

  const { theme, toggleTheme } = useTheme();

  const fetchWithRetry = async (fn, retries = 5, baseDelay = 1000) => {
    for (let i = 0; i < retries; i++) {
      try {
        return await fn();
      } catch (error) {
        if (error.status === 429) {
          const delay = Math.min(baseDelay * 2 ** i, 5000);
          console.log(`Retry ${i + 1}/${retries} after ${delay}ms due to 429 error`);
          await new Promise(resolve => setTimeout(resolve, delay));
        } else {
          throw error;
        }
      }
    }
    throw new Error('Max retries exceeded');
  };

  const sendFrontendLog = (action, extra = {}) => {
    if (socket && socket.connected && account) {
      socket.emit('frontendLog', {
        user: account,
        action,
        timestamp: new Date().toISOString(),
        ...extra
      });
    } else {
      console.warn('Cannot send log: socket not ready or no account');
    }
  };

  // ────────────────────────────────────────────────────────────────
  // Socket.io Setup (connect, disconnect, sensorData)
  // ────────────────────────────────────────────────────────────────

  // Socket.IO debug logs
  useEffect(() => {
    socket.connect();

    const onConnect = () => console.log('Socket.IO connected');
    const onError = (error) => console.error('Socket.IO connect error:', error);
    const onDisconnect = () => console.log('Socket.IO disconnected');

    socket.on('connect', onConnect);
    socket.on('connect_error', onError);
    socket.on('disconnect', onDisconnect);

    return () => {
      socket.disconnect();
      socket.off('connect', onConnect);
      socket.off('connect_error', onError);
      socket.off('disconnect', onDisconnect);
    };
  }, []);

  // Periodic task refresh
  useEffect(() => {
    const interval = setInterval(() => setLoadTasks(true), 60000);
    return () => clearInterval(interval);
  }, []);


  // WebSocket for sensor data
  useEffect(() => {
    socket.on('sensorData', (data) => {
      console.log('Received sensor data:', data);
      setTasks((prevTasks) =>
        prevTasks.map((task) =>
          task.taskId === data.taskId
            ? { ...task, sensorData: { pir: data.pir, light: data.light } }
            : task
        )
      );
    });
    return () => {
      socket.off('sensorData');
    };
  }, []);

  // ────────────────────────────────────────────────────────────────
  // Wallet Connection & Task Fetching
  // ────────────────────────────────────────────────────────────────

  // Wallet and task fetching
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchTasks = async () => {
      try {
        setLoadingTasks(true);
        const result = await fetchWithRetry(() =>
          publicClient.readContract({
            address: CONTRACT_ADDRESS,
            abi: MyContractABI,
            functionName: 'getAllTasks',
          })
        );
        setLoadingTasks(false);
        setTasks(result);
        if (DEBUG) console.log('Tasks fetched:', result);
      } catch (error) {
        console.error('Σφάλμα ανάκτησης tasks:', error);
      }
    };

    if (!loadTasks) return;

    fetchTasks();
    setLoadTasks(false);
  }, [loadTasks]);

  // ────────────────────────────────────────────────────────────────
  // Backend Communication (sendTaskToBackend)
  // ────────────────────────────────────────────────────────────────

  const sendTaskToBackend = async (payload) => {
    try {
      await fetch('/api/task', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: safeStringify(payload),
      });
      console.log('Task sent to backend:', payload);
    } catch (err) {
      console.error('Failed to send task to backend:', err);
    }
  };

  const openCompleteModal = (taskId) => {
    setCompleteTaskId(taskId);
    setAfterPhoto(null);
  };

  // ────────────────────────────────────────────────────────────────
  // Create Task
  // ────────────────────────────────────────────────────────────────

  const handleCreateTask = async () => {
    if (!account) {
     alert('Παρακαλώ συνδέστε το πορτοφόλι σας');
      return;
    }
    if (!beforePhoto) {
      alert("Πρέπει να ανεβάσεις before photo");
      return;
    }
    if (!taskDescription || !taskValue) {
      alert('Παρακαλώ συμπληρώστε όλα τα πεδία');
      return;
    }
    if (
      positionX === '' ||
      positionY === '' ||
      positionZ === ''
    ) {
      alert('Συμπλήρωσε τις συντεταγμένες x, y, z');
      return;
    }

    const startTime = Date.now();  // Συνολική έναρξη
    // 🔥 Για σενάρια 1,4,5,6 → κανονική ροή με φωτογραφία
    try {
      // ✅ Βεβαιώσου ότι είμαστε στο Besu EduNet
      await ensureBesuNetwork();

      const valueInWei = parseEther(taskValue);
      const { request } = await fetchWithRetry(() =>
        publicClient.simulateContract({
          account,
          address: CONTRACT_ADDRESS,
          abi: MyContractABI,
          functionName: 'createTask',
          args: [
            taskDescription,
            Number(selectedSpaceType),
            Number(selectedCategory)
          ],
          value: valueInWei,
        })
      );
      const simEndTime = Date.now();  // Τέλος simulation (optional, για per-step)
      console.log(`Blockchain simulation latency for createTask: ${simEndTime - startTime} ms`);

      const hash = await walletClient.writeContract({
        ...request,
        account,
      });
      const txEndTime = Date.now();  // Τέλος write (sending tx)
      console.log(`Blockchain write latency for createTask: ${txEndTime - simEndTime} ms`);

      const receipt = await publicClient.waitForTransactionReceipt({ hash });
      const waitEndTime = Date.now();  // Τέλος wait (confirmation)
      console.log('Task created on blockchain, tx hash:', hash);
      console.log(`Gas used: ${receipt.gasUsed.toString()} wei`);
      console.log(`Blockchain wait latency for createTask: ${waitEndTime - txEndTime} ms`);

      // ΣΩΣΤΟ PARSING ΜΕ parseEventLogs (utility από viem)
      const logs = parseEventLogs({
        abi: MyContractABI,
        logs: receipt.logs,
      });

      const taskCreatedLog = logs.find(log => log.eventName === 'TaskCreated');

      if (!taskCreatedLog) {
        throw new Error('Δεν βρέθηκε TaskCreated event! Ελέγξτε αν το contract εκπέμπει σωστά.');
      }

      const newTaskId = Number(taskCreatedLog.args.taskId);
      // 2. Upload BEFORE photo with taskId
      const formData = new FormData();
      formData.append("taskId", newTaskId);
      formData.append("category", selectedCategory);
      formData.append("photo", beforePhoto);

      await fetch("/api/upload/before", {
        method: "POST",
        body: formData,
      });

      if (DEBUG) console.log('Creating task on blockchain...');
      console.log('Νέο task δημιουργήθηκε με taskId:', newTaskId);
      // μετά επιτυχές createTask
      sendFrontendLog('task_created', { taskId: newTaskId });

      console.log('Fetching updated tasks...');
      const updatedTasks = await fetchWithRetry(() =>
        publicClient.readContract({
          address: CONTRACT_ADDRESS,
          abi: MyContractABI,
          functionName: 'getAllTasks',
        })
      );
      setTasks(updatedTasks);
      console.log('Updated tasks:', updatedTasks);
      sendTaskToBackend({
        taskId: newTaskId,
        action: "create",
        
        position: {
          x: Number(positionX),
          y: Number(positionY),
          z: Number(positionZ)
        }
      });

      setTaskDescription('');
      setTaskValue('0.01');
      setBeforePhoto(null);

      setPositionX('');
      setPositionY('');
      setPositionZ('');

      const totalEndTime = Date.now();
      console.log(`Total blockchain latency for createTask: ${totalEndTime - startTime} ms`);
    } catch (error) {
      console.error('Σφάλμα δημιουργίας task:', error);
    }
  };

  // ────────────────────────────────────────────────────────────────
  // Accept Task
  // ────────────────────────────────────────────────────────────────

  const handleAcceptTask = async (taskId) => {
    if (!account) return alert("Σύνδεσε πορτοφόλι");

    const task = tasks.find(t => Number(t.taskId) === Number(taskId));
    const category = task?.category ?? selectedCategory;
    const spaceType = task?.spaceType ?? selectedSpaceType;

    setTasks(prev => prev.map(t =>
      t.taskId === taskId ? { ...t, accepting: true } : t
    ));

    try {
      await ensureBesuNetwork();

      const { request } = await publicClient.simulateContract({
        account,
        address: CONTRACT_ADDRESS,
        abi: MyContractABI,
        functionName: 'acceptTask',
        args: [taskId],
      });
      const hash = await walletClient.writeContract(request);
      console.log('Accept tx sent:', hash);
      await publicClient.waitForTransactionReceipt({ hash });

      // ✅ Καθαρό send
      sendTaskToBackend({
        taskId,
        action: "accept",
        category,
        spaceType
      });

    } catch (err) {
      console.error(err);
      alert('Αποτυχία: ' + (err.shortMessage || err.message));
    } finally {
      setTasks(prev => prev.map(t =>
        t.taskId === taskId ? { ...t, accepting: false } : t
      ));
      setLoadTasks(true);
    }
  };

  const handleUploadDuring = async (taskId) => {
    if (!duringPhoto) return alert("Διάλεξε φωτογραφία");

    const task = tasks.find(t => Number(t.taskId) === Number(taskId));
    const category = task?.category ?? selectedCategory; // fallback αν χρειαστεί

    const formData = new FormData();
    formData.append("taskId", taskId);
    formData.append("category", category);
    formData.append("photo", duringPhoto);

    await fetch("/api/upload/during", {
      method: "POST",
      body: formData,
    });

    alert("During photo uploaded!");
  };

  // ────────────────────────────────────────────────────────────────
  // Complete Task (modal + upload)
  // ────────────────────────────────────────────────────────────────

  const handleCompleteTaskOnChain = async (taskId) => {
    try {
      await ensureBesuNetwork();

      const { request } = await publicClient.simulateContract({
        account,
        address: CONTRACT_ADDRESS,
        abi: MyContractABI,
        functionName: "completeTask",
        args: [taskId], // always false — validation pending
      });
      const hash = await walletClient.writeContract(request);
      await publicClient.waitForTransactionReceipt({ hash });

      console.log("Task completed on-chain:", hash);
    } catch (err) {
      console.error("Blockchain completeTask error:", err);
      throw err;
    }
  };

    const handleSubmitAfterPhoto = async () => {
    if (!afterPhoto) {
      alert("Πρέπει να ανεβάσεις after photo");
      return;
    }

    try {
      const task = tasks.find(t => Number(t.taskId) === Number(completeTaskId));
      const category = task?.category ?? selectedCategory;

      const formData = new FormData();
      formData.append("taskId", completeTaskId);
      formData.append('category', category);
      formData.append("photo", afterPhoto);
      formData.append("wallet", account);

      // 1. Upload after photo στο backend
      const uploadRes = await fetch("/api/upload/after", {
        method: "POST",
        body: formData,
      });

      const uploadData = await uploadRes.json();
      console.log("After photo uploaded:", uploadData);

      // 2. On-chain completeTask(false)
      await handleCompleteTaskOnChain(completeTaskId);

      // 3. Ενημέρωση backend ότι το task ολοκληρώθηκε
      await sendTaskToBackend({
        taskId: completeTaskId,
        action: "complete"
      });

      alert("Το task ολοκληρώθηκε!");

      setAfterPhoto(null);
      setLoadTasks(true);
    } catch (err) {
      console.error(err);
      alert("Αποτυχία ολοκλήρωσης");
    }
  };

  // ────────────────────────────────────────────────────────────────
  // Pay Task
  // ────────────────────────────────────────────────────────────────

  const handlePayTask = async (taskIndex) => {
    const task = tasks[taskIndex];
    if (!task) {
      console.error('Task not found for taskIndex:', taskIndex);
      alert(`Δεν βρέθηκε task ${taskIndex}`);
      return;
    }

    const { taskId } = task;
    if (!account) {
      alert('Παρακαλώ συνδέστε το πορτοφόλι σας');
      return;
    }
    const startTime = Date.now();  // Συνολική έναρξη
    try {
      if (task.reviewStatus !== 1) {
          alert("Το task πρέπει πρώτα να εγκριθεί από τον admin.");
          return;
      }
      await ensureBesuNetwork();
      console.log('Paying task on blockchain:', taskId);
      const { request } = await fetchWithRetry(() =>
        publicClient.simulateContract({
          account,
          address: CONTRACT_ADDRESS,
          abi: MyContractABI,
          functionName: 'payTask',
          args: [taskId],
        })
      );
      const simEndTime = Date.now();  // Τέλος simulation (optional, για per-step)
      console.log(`Blockchain simulation latency for payTask: ${simEndTime - startTime} ms`);

      const hash = await walletClient.writeContract({
        ...request,
        account,
      });
      const txEndTime = Date.now();  // Τέλος write (sending tx)
      console.log(`Blockchain write latency for payTask: ${txEndTime - simEndTime} ms`);

      const receipt = await publicClient.waitForTransactionReceipt({ hash });
      console.log('Task paid, tx hash:', hash);
      console.log(`Gas used: ${receipt.gasUsed.toString()} wei`);
      const waitEndTime = Date.now();  // Τέλος wait (confirmation)
      console.log(`Blockchain wait latency for payTask: ${waitEndTime - txEndTime} ms`);

      await sendTaskToBackend({
        taskId,
        action: "pay"
      });

      console.log('Fetching updated tasks...');
      const updatedTasks = await fetchWithRetry(() =>
        publicClient.readContract({
          address: CONTRACT_ADDRESS,
          abi: MyContractABI,
          functionName: 'getAllTasks',
        })
      );
      setTasks(updatedTasks);
      const totalEndTime = Date.now();
      console.log(`Total blockchain latency for payTask: ${totalEndTime - startTime} ms`);
    } catch (error) {
      console.error('Σφάλμα πληρωμής task:', error);
    }
  };

  return (
    <div className="App">
      {/* ──────────────────────────────────────────────────────────────── */}
      {/* UI: Header & Theme Toggle */}
      {/* ──────────────────────────────────────────────────────────────── */}
      <header className="App-header">
        <img src={logo} className="App-logo" alt="logo" />
        <p>My DApp</p>
        <button className="action-button" onClick={toggleTheme}>
          {theme === 'light' ? 'Σκούρο Θέμα' : 'Φωτεινό Θέμα'}
        </button>

        {account ? (
          <>
            <p>Συνδεδεμένος λογαριασμός: {account}</p>

            {account.toLowerCase() === "0x3aBC06F18a86Fc1038A28547283488B64beff5f2".toLowerCase() && (
              <button
                className="action-button"
                style={{ backgroundColor: '#ff9800', marginLeft: '10px' }}
                onClick={() => window.location.href = '/admin'}
              >
                🔑 Admin Panel
              </button>
            )}
          </>
        ) : (
          <p>Παρακαλώ συνδεθείτε με το πορτοφόλι σας.</p>
        )}
      </header>
      {account && (
        <>
          <div className="input-container">
          {/* ──────────────────────────────────────────────────────────────── */}
          {/* UI: Create Task Form */}
          {/* ──────────────────────────────────────────────────────────────── */}
          <input
            className="value-input"
            value={taskDescription}
            onChange={(e) => setTaskDescription(e.target.value)}
            placeholder="Περιγραφή Task"
          />
          <input
            className="value-input"
            value={taskValue}
            onChange={(e) => setTaskValue(e.target.value)}
            placeholder="Αξία σε ETH"
          />
          {/* Dropdown για Category */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(Number(e.target.value))}
          >
            <option value={0}>Unknown</option>
            <option value={1}>Cleaning</option>
            <option value={2}>Cooking</option>
            <option value={3}>Laundry</option>
            <option value={4}>Maintenance</option>
            <option value={5}>Organization</option>
            <option value={6}>Other</option>
          </select>
          <select value={selectedSpaceType} onChange={(e) => setSelectedSpaceType(Number(e.target.value))}>
            <option value={0}>Private</option>
            <option value={1}>Common</option>
            <option value={2}>Public</option>
          </select>
          <input
            className="value-input"
            type="number"
            step="0.1"
            value={positionX}
            onChange={(e) => setPositionX(e.target.value)}
            placeholder="Position X"
          />
          <input
            className="value-input"
            type="number"
            step="0.1"
            value={positionY}
            onChange={(e) => setPositionY(e.target.value)}
            placeholder="Position Y"
          />
          <input
            className="value-input"
            type="number"
            step="0.1"
            value={positionZ}
            onChange={(e) => setPositionZ(e.target.value)}
            placeholder="Position Z"
          />
          <>
          <label>Before Photo (αρχική κατάσταση):</label>
             <input
               type="file"
               accept="image/*"
               onChange={(e) => setBeforePhoto(e.target.files[0])}
             />
           </>
           {completeTaskId && (
             <div className="modal-overlay">
               {/* ──────────────────────────────────────────────────────────────── */}
               {/* UI: Complete Task Modal */}
               {/* ──────────────────────────────────────────────────────────────── */}
               <div className="modal-content">
                 <h3>Ολοκλήρωση Task {completeTaskId}</h3>
                 <p>Ανέβασε After Photo:</p>
                   <input
                     type="file"
                     accept="image/*"
                     onChange={(e) => setAfterPhoto(e.target.files[0])}
                   />
                   <div style={{ marginTop: '10px' }}>
                     <button onClick={handleSubmitAfterPhoto}>Υποβολή Ολοκλήρωσης</button>
                     <button onClick={() => setCompleteTaskId(null)}>Άκυρο</button>
                   </div>
               </div>
             </div>
           )}
          <button className="action-button" onClick={handleCreateTask}>
            Δημιουργία Task
          </button>
        </div>
        <div className="task-container">
            {/* ──────────────────────────────────────────────────────────────── */}
            {/* UI: Task List */}
            {/* ──────────────────────────────────────────────────────────────── */}
            <button
              className="action-button"
              style={{
                marginBottom: '10px',
                width: '100%',
                maxWidth: '400px',
                alignSelf: 'center'
              }}
              onClick={() => setLoadTasks(true)}
            >
              🔄 Ανάκτηση Tasks
            </button>
            <h2>Tasks</h2>
            {loadingTasks && <p>Φόρτωση tasks...</p>}
            <ul className="task-list">
              {tasks.map((task, index) => (
                <li key={index} className="task-item">
                  <span>
                    Space: {['Private', 'Common', 'Public'][task.spaceType]}
                  </span>
                  <span>
                    Task {index}: {task.description} ({task.value.toString()} wei) - Κατάσταση: {['Open', 'Accepted', 'Completed', 'Paid'][task.status]}
                  </span>
                  <span>
                    Review: {['Pending', 'Accepted', 'Rejected'][task.reviewStatus]}
                  </span>
                  {task.reviewStatus === 2 && (
                    <span style={{ color: 'red' }}>
                      Reason: {task.reviewReason}
                    </span>
                  )}
                  <span>
                    Category: {['Unknown','Cleaning','Cooking','Laundry','Maintenance','Organization','Other'][task.category]}
                  </span>
                  <div>
                    {/* Accept Button */}
                    {task.status === 0 && account !== task.creator && (
                      <>
                        <button
                          onClick={() => {
                            console.log('Accept clicked for taskId:', Number(task.taskId));
                            handleAcceptTask(task.taskId);
                          }}
                          disabled={task.accepting}
                        >
                          {task.accepting ? 'Αποδοχή...' : 'Αποδοχή Task'}
                        </button>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => setDuringPhoto(e.target.files[0])}
                        />
                        <button onClick={() => handleUploadDuring(task.taskId)}>
                          Upload During Photo
                        </button>
                      </>
                    )}
                    {/* Complete Button */}
                    {task.status === 1 && account === task.executor && (
                      <>
                        <button onClick={() => openCompleteModal(task.taskId)}>
                          Ολοκλήρωση Task
                        </button>
                      </>
                    )}
                    {/* Pay Button */}
                    {task.status === 2 && task.reviewStatus === 1 && account === task.creator && (
                      <button onClick={() => handlePayTask(index)}>
                        Πληρωμή Task
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <TransactionHistory />
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// New Component
// ────────────────────────────────────────────────────────────────

function AppContent() {
  const [account, setAccount] = useState(null);

  useEffect(() => {
    const connectWallet = async () => {
      try {
        const [acc] = await walletClient.requestAddresses();
        setAccount(acc);
      } catch (err) {
        console.error("Wallet connect error:", err);
      }
    };

    connectWallet();
  }, []);

  return (
    <Routes>
      <Route
        path="/"
        element={
          <AppMainContent
            account={account}
            setAccount={setAccount}
          />
        }
      />
      <Route
        path="/admin"
        element={
          <AdminPage
            account={account}
            CONTRACT_ADDRESS={CONTRACT_ADDRESS}
            MyContractABI={MyContractABI}
            publicClient={publicClient}
            walletClient={walletClient}
          />
        }
      />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

// ────────────────────────────────────────────────────────────────
// Export Component
// ────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <ThemeProvider>
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
}
